import { useCallback, useState } from "react";
import { notifyError, notifySuccess, notifyWarning } from "../utils/notify";
import { useApp } from "../context/AppContext";
import { getApiUrl, API_CONFIG } from "../config/api";
import { SendFormLinkResponse } from "../types/formTemplates";

interface UseSendFormReminderOptions {
  /** Called after a successful send so the caller can refresh derived UI
   *  (e.g. the modal's scheduled-messages list). */
  onSent?: () => void | Promise<void>;
}

/**
 * Single-source "Send Forms to Client" flow, shared by the Transaction Modal's
 * ScheduledRemindersPanel and the calendar appointment drawer so consent
 * handling can't drift.
 *
 * Optimistic: POST without override; if the backend returns
 * {sent:false, reason:"opted_out"}, surface the verbal-confirmation modal and
 * let staff re-POST with verbal_confirmation_from_client=true. The backend is
 * the single source of truth for eligibility — no frontend pre-check of
 * is_opted_in_reminder.
 */
export function useSendFormReminder(
  transactionUuid: string,
  { onSent }: UseSendFormReminderOptions = {},
) {
  const { fetchWithAuth } = useApp();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [verbalConfirmOpen, setVerbalConfirmOpen] = useState(false);

  const postSendFormLink = useCallback(
    async (
      verbalConfirmation: boolean,
    ): Promise<SendFormLinkResponse | null> => {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.SEND_FORM_LINK, "", {
        transactionUuid,
      });
      return (await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(
          verbalConfirmation ? { verbal_confirmation_from_client: true } : {},
        ),
      })) as SendFormLinkResponse | null;
    },
    [fetchWithAuth, transactionUuid],
  );

  const handleResult = useCallback(
    (
      result: SendFormLinkResponse | null,
      fromOverride: boolean,
    ): "opted_out" | "handled" => {
      if (result?.sent) {
        notifySuccess("Client has been texted the forms link.", {
          title: "Form reminder sent",
        });
        return "handled";
      }
      if (result?.reason === "no_contact") {
        notifyWarning(
          "Client has no phone number on file — add a phone to send.",
          { title: "Could not send" },
        );
        return "handled";
      }
      if (result?.reason === "opted_out" && !fromOverride) {
        return "opted_out";
      }
      notifyError("Could not send form link. Try again.", {
        title: "Could not send",
      });
      return "handled";
    },
    [],
  );

  const sendReminder = useCallback(async () => {
    setSending(true);
    try {
      const result = await postSendFormLink(false);
      const outcome = handleResult(result, false);
      if (outcome === "opted_out") {
        setVerbalConfirmOpen(true);
        return;
      }
      if (result?.sent) {
        setSent(true);
        await onSent?.();
      }
    } catch (err: any) {
      console.error("Failed to send form link:", err);
      notifyError(err?.errorBody?.message ?? "Could not send form link.", {
        title: "Could not send",
      });
    } finally {
      setSending(false);
    }
  }, [postSendFormLink, handleResult, onSent]);

  const confirmVerbalSend = useCallback(async () => {
    setSending(true);
    try {
      const result = await postSendFormLink(true);
      handleResult(result, true);
      setVerbalConfirmOpen(false);
      if (result?.sent) {
        setSent(true);
        await onSent?.();
      }
    } catch (err: any) {
      console.error("Failed to send form link with override:", err);
      notifyError(err?.errorBody?.message ?? "Could not send form link.", {
        title: "Could not send",
      });
    } finally {
      setSending(false);
    }
  }, [postSendFormLink, handleResult, onSent]);

  return {
    sending,
    sent,
    verbalConfirmOpen,
    setVerbalConfirmOpen,
    sendReminder,
    confirmVerbalSend,
  };
}
